import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';

const PLANS = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    desc: 'Perfect for solo PR professionals getting started.',
    highlight: false,
    cta: 'Get started free',
    ctaTo: '/register',
    features: [
      '100 contacts',
      '50 messages/month',
      'Basic reporter database access',
      'Press lists (up to 3)',
      'Coverage tracking',
      'Email support',
    ],
    missing: ['AI pitch drafting', 'Unlimited reporter search', 'Client management', 'Team members', 'Analytics', 'Slack integration'],
  },
  {
    name: 'Pro',
    price: '$40',
    period: '/month',
    desc: 'For growing agencies that need more power and fewer limits.',
    highlight: true,
    cta: 'Start 14-day free trial',
    ctaTo: '/register',
    badge: 'Most popular',
    features: [
      'Unlimited contacts',
      '2,000 messages/month',
      'Full reporter database access',
      'Unlimited press lists',
      'Coverage tracking + reporting',
      'AI pitch drafting',
      'Gmail + IMAP integration',
      'Priority email support',
    ],
    missing: ['Multiple team members', 'Client management', 'Slack integration'],
  },
  {
    name: 'Team',
    price: '$30',
    period: '/seat/month',
    desc: 'For collaborative teams managing multiple clients.',
    highlight: false,
    cta: 'Start 14-day free trial',
    ctaTo: '/register',
    features: [
      'Everything in Pro',
      'Multiple team members',
      'Per-client dashboards',
      'Client management',
      'Team activity analytics',
      'Slack integration',
      'Role-based permissions',
      'Dedicated onboarding',
    ],
    missing: [],
  },
];

const COMPARE_ROWS = [
  { feature: 'Contacts', free: '100', pro: 'Unlimited', team: 'Unlimited' },
  { feature: 'Messages/month', free: '50', pro: '2,000', team: 'Unlimited' },
  { feature: 'Press lists', free: 'Up to 3', pro: 'Unlimited', team: 'Unlimited' },
  { feature: 'Reporter database', free: 'Limited', pro: 'Full access', team: 'Full access' },
  { feature: 'AI pitch drafting', free: false, pro: true, team: true },
  { feature: 'Coverage tracking', free: true, pro: true, team: true },
  { feature: 'Client management', free: false, pro: false, team: true },
  { feature: 'Team members', free: '1', pro: '1', team: 'Unlimited' },
  { feature: 'Slack integration', free: false, pro: false, team: true },
  { feature: 'Analytics', free: 'Basic', pro: 'Advanced', team: 'Advanced' },
  { feature: 'Support', free: 'Email', pro: 'Priority email', team: 'Dedicated' },
];

function Check() {
  return (
    <svg className="w-5 h-5 text-green-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function Cross() {
  return (
    <svg className="w-5 h-5 text-gray-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CellValue({ val }: { val: boolean | string }) {
  if (val === true) return <Check />;
  if (val === false) return <Cross />;
  return <span className="text-sm text-gray-700 text-center block">{val}</span>;
}

export default function PricingPage() {
  useEffect(() => {
    document.title = 'Pricing — PitchFriendly';
  }, []);

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="bg-white border-b border-gray-100 py-16 text-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Simple, honest pricing</h1>
          <p className="text-gray-500 text-lg">Start free. Upgrade when you need more. No hidden fees, no long-term contracts.</p>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl border p-8 flex flex-col ${
                  plan.highlight
                    ? 'bg-blue-600 border-blue-600 shadow-xl shadow-blue-600/20'
                    : 'bg-white border-gray-200'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full shadow">
                      {plan.badge}
                    </span>
                  </div>
                )}
                <div className="mb-6">
                  <h2 className={`text-xl font-bold mb-1 ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h2>
                  <div className="flex items-end gap-1 mb-2">
                    <span className={`text-4xl font-extrabold ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>{plan.price}</span>
                    <span className={`text-sm pb-1 ${plan.highlight ? 'text-blue-200' : 'text-gray-400'}`}>{plan.period}</span>
                  </div>
                  <p className={`text-sm ${plan.highlight ? 'text-blue-200' : 'text-gray-500'}`}>{plan.desc}</p>
                </div>

                <Link
                  to={plan.ctaTo}
                  className={`block w-full py-3 px-4 rounded-xl text-sm font-semibold text-center transition-colors mb-8 ${
                    plan.highlight
                      ? 'bg-white text-blue-600 hover:bg-blue-50'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {plan.cta}
                </Link>

                <ul className="space-y-3 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.highlight ? 'text-blue-200' : 'text-blue-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className={`text-sm ${plan.highlight ? 'text-blue-100' : 'text-gray-700'}`}>{f}</span>
                    </li>
                  ))}
                  {plan.missing.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 opacity-40">
                      <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.highlight ? 'text-blue-300' : 'text-gray-300'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className={`text-sm line-through ${plan.highlight ? 'text-blue-200' : 'text-gray-400'}`}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-center mt-6 text-xs text-gray-400">
            All paid plans include a 14-day free trial · Cancel anytime · Billed monthly
          </p>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">Full feature comparison</h2>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 w-1/2">Feature</th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700">Free</th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-blue-600 bg-blue-50">Pro</th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700">Team</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {COMPARE_ROWS.map((row) => (
                  <tr key={row.feature} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3.5 text-sm text-gray-800">{row.feature}</td>
                    <td className="px-4 py-3.5"><CellValue val={row.free} /></td>
                    <td className="px-4 py-3.5 bg-blue-50/50"><CellValue val={row.pro} /></td>
                    <td className="px-4 py-3.5"><CellValue val={row.team} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">Frequently asked questions</h2>
          <div className="space-y-6">
            {[
              {
                q: 'Can I try Pro before paying?',
                a: 'Yes — every new account gets a 14-day free trial of Pro features. No credit card required.',
              },
              {
                q: 'What happens when my trial ends?',
                a: 'Your account automatically moves to the Free plan. Your data is never deleted. You can upgrade anytime.',
              },
              {
                q: 'How does Team pricing work?',
                a: 'Team is billed per seat per month. Add seats as your team grows, remove them if you downsize. Minimum 2 seats.',
              },
              {
                q: 'Do you offer annual billing?',
                a: 'Annual billing is coming soon and will include a discount. Contact us at hello@pitchfriendly.com to be notified.',
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit cards via Stripe. For annual invoicing on Team plans, contact us.',
              },
            ].map((item) => (
              <div key={item.q} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600 text-white text-center">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold mb-4">Start free today</h2>
          <p className="text-blue-100 mb-8">No credit card required. Upgrade when you\'re ready.</p>
          <Link to="/register" className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-colors">
            Create free account
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
